/**
 * How long a session lives, and what counts as being here.
 *
 * ── WHY THIS FILE EXISTS AT ALL ──────────────────────────────────────────────────────────────
 * The idle policy used to be written down TWICE, in two files that never spoke: `25 * 60 * 1000`
 * in useSessionTimeout.js and a bare `26 * 60 * 1000` literal in tokenRefresh.js's silent-refresh
 * brake. Two copies of a number is two policies; making one of them per-tenant while the other
 * stayed a literal would have produced a session that the timer ends at eleven minutes while the
 * interceptor was still happily extending the token. There is one window now, and it is resolved
 * here.
 *
 * ── AND WHY IT IS A SETTING ──────────────────────────────────────────────────────────────────
 * Two construction companies answer this differently. A firm running shared trailer laptops on an
 * open site wants a short leash; an office of assigned machines behind a locked door does not. So
 * the window is `identity_tenant_settings.session_idle_minutes`, NULL by default, and the numbers
 * below are what a tenant who has never touched it gets.
 *
 * ── THE DEFAULTS, AND THE EVIDENCE FOR THEM ──────────────────────────────────────────────────
 * IDLE = 60 MINUTES. The previous window was 25, and it threw working people out — that is the
 * incident this whole change exists to not repeat. Most of that was a broken activity signal
 * (see below) rather than the length, but "most" is not a number I can stand behind, and the cost
 * of guessing low is the exact failure that already cost this company trust. Sixty is more than
 * double the window that hurt them, so it cannot recreate that bug on its own; it still closes out
 * a laptop left open in a truck within the hour; and it is unremarkable for business software
 * holding payroll and pricing (banking sits at 10-15, general SaaS at 30-60).
 *
 * ABSOLUTE = 12 HOURS. Independent of activity: a session that has simply been alive this long
 * ends, so somebody signs in once a working day. This is the half that answers "a closed laptop
 * must not still be signed in fifteen hours later" even if the idle clock is ever wrong again, and
 * a tenant who works genuinely long shifts can raise it.
 *
 * IT IS ALSO ENFORCED ON THE SERVER NOW, and only this half is. `/auth/refresh` checks an
 * `auth_time` claim against the tenant's maximum, so the credential itself stops working — see
 * src/modules/identity/backend/services/session_policy.py. The idle window below remains a purely
 * browser-side rule: it says when the app signs somebody out on their own screen, not how long a
 * copied session stays usable.
 *
 * ⚠ THIS PATH GIVES NO WARNING, AND THE HEADER USED TO CLAIM IT GAVE THE SAME ONE AS THE IDLE
 * PATH. It does not. `idleVerdict` below returns 'expired' the moment the absolute window is
 * reached, before the 'warning' branch is reachable at all, and `useSessionTimeout` then signs the
 * person out with a message that says it was "due to inactivity" — which on this path is untrue.
 * `secondsUntilExpiry` already counts the absolute window down correctly, so the modal would work
 * if a verdict ever asked for it. Left as-is deliberately rather than fixed in passing: it is a
 * behaviour change to the timeout hook with its own blast radius. Noted so the next reader finds a
 * known defect rather than a promise the code does not keep.
 *
 * WARNING = 2 MINUTES. Was 60 seconds. A minute is not enough time to notice a modal, read it and
 * decide, on a phone in a pocket or a second monitor nobody is looking at.
 *
 * ── WHAT COUNTS AS BEING HERE ────────────────────────────────────────────────────────────────
 * Activity used to mean four DOM events on `window`: mousedown, keydown, scroll, touchstart. That
 * is a much narrower signal than it reads:
 *   - `scroll` does not bubble from elements, so scrolling any of the app's ~89 inner scroll panes
 *     registered NOTHING. Reading a long field ticket looked exactly like an empty chair. The
 *     listeners are registered in the CAPTURE phase on `document` now, which does reach them.
 *   - wheel, click, pointerdown, input and change were not tracked at all.
 *   - No API call, of any kind, counted. A twenty-minute upload, a slow report, a person working
 *     steadily through a form that saves as they go — all idle, by the clock's reckoning.
 * `markApiActivity` fixes the last one, and it is deliberately narrow: only requests that CHANGE
 * something count. A background poller doing GETs must never hold a session open, or the timeout
 * is decorative — and this app has three pollers running on their own timers.
 *
 * ── "I DO NOT KNOW" IS NOT "EVERYTHING IS FINE" ──────────────────────────────────────────────
 * `lastActivityAt()` returns null when there is no record, and `idleVerdict` reports that as
 * 'unknown', which every caller treats as expired. The old code read an absent key and silently
 * granted a fresh full window to a session of unknown age — which is precisely how a fifteen-hour
 * session stayed signed in.
 */
import { APP_STORAGE_PREFIX, TENANT_BRANDING_STORAGE_KEY } from '../../config/config.js'

/** The window a tenant gets when they have never set one. See the header for the reasoning. */
export const DEFAULT_IDLE_MINUTES = 60

/**
 * ── THIS NUMBER IS ALSO WRITTEN DOWN IN PYTHON ───────────────────────────────────────────────
 * The absolute cap is now enforced on the SERVER as well as here, so the same twelve has to exist
 * in src/modules/identity/backend/services/session_policy.py (DEFAULT_ABSOLUTE_HOURS), and the
 * bounds below have to match its MIN/MAX_ABSOLUTE_HOURS and the validator in routers/branding.py.
 *
 * There is no Python-to-JavaScript constant sharing in this repo — the language-tag rule is the
 * nearest precedent and it is likewise two implementations pointing at each other. Comments do not
 * fail a build, so the agreement is enforced by a test:
 * src/modules/identity/backend/tests/test_the_session_cap_is_the_number_the_screen_promised.py.
 *
 * If they drift: THIS number is what the admin screen showed a customer as "our default"; the
 * Python one is what actually signs them out. Decide which is right and change both.
 */
export const DEFAULT_ABSOLUTE_HOURS = 12
export const WARNING_MS = 2 * 60 * 1000

/** Bounds. A tenant may not configure the protection away, nor set one so short it evicts people. */
const MIN_IDLE_MINUTES = 5
const MAX_IDLE_MINUTES = 24 * 60
/** Mirrored in services/session_policy.py — see DEFAULT_ABSOLUTE_HOURS above. */
const MIN_ABSOLUTE_HOURS = 1
const MAX_ABSOLUTE_HOURS = 24 * 7

const LAST_ACTIVITY_KEY = `${APP_STORAGE_PREFIX}_last_activity`
const SESSION_STARTED_KEY = `${APP_STORAGE_PREFIX}_session_started`

export const SESSION_KEYS = Object.freeze({
  lastActivity: LAST_ACTIVITY_KEY,
  sessionStarted: SESSION_STARTED_KEY,
})

/**
 * Read one number out of the cached tenant branding blob.
 *
 * That blob is the app's only PRE-AUTH, host-resolved settings payload (TenantContext fetches
 * `/api/identity/branding?domain=…` and caches it), and `TenantProvider` sits above `<App/>`, so
 * the value is on hand at first paint with no extra request and no gap during which a session
 * would run on the wrong window. Anything unreadable, absent, non-numeric or out of bounds falls
 * back to the default — a corrupt cache must never be able to shorten or abolish the window.
 */
function tenantMinutes(field, fallback, min, max) {
  try {
    const raw = window.localStorage.getItem(TENANT_BRANDING_STORAGE_KEY)
    if (!raw) return fallback
    const value = JSON.parse(raw)?.[field]
    if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
    return Math.min(max, Math.max(min, Math.round(value)))
  } catch {
    return fallback
  }
}

/** How long somebody may do nothing before the warning appears. */
export function idleMs() {
  return tenantMinutes('sessionIdleMinutes', DEFAULT_IDLE_MINUTES, MIN_IDLE_MINUTES, MAX_IDLE_MINUTES) * 60 * 1000
}

/** How long a session may live regardless of how busy it is. */
export function absoluteMs() {
  return tenantMinutes('sessionAbsoluteHours', DEFAULT_ABSOLUTE_HOURS, MIN_ABSOLUTE_HOURS, MAX_ABSOLUTE_HOURS) * 60 * 60 * 1000
}

/** Idle window plus the grace period — the moment the session actually ends. */
export function expiryMs() {
  return idleMs() + WARNING_MS
}

function readStamp(key) {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const at = parseInt(raw, 10)
    return Number.isFinite(at) ? at : null
  } catch {
    return null
  }
}

function writeStamp(key, at) {
  try {
    window.localStorage.setItem(key, String(at))
  } catch { /* storage disabled by policy; the in-memory clock still runs */ }
}

/**
 * When this person was last doing something.
 *
 * NULL MEANS UNKNOWN, and unknown is not innocent — see `idleVerdict`. Every caller must keep the
 * two apart, which is why this returns null rather than `Date.now()`.
 */
export function lastActivityAt() {
  return readStamp(LAST_ACTIVITY_KEY)
}

/** Record that a human did something. Also syncs the other tabs, via the storage event. */
export function markActivity(at) {
  writeStamp(LAST_ACTIVITY_KEY, at || Date.now())
}

/**
 * When this session began — the anchor for the absolute cap.
 *
 * ADOPTS RATHER THAN EXPIRES when the stamp is missing. Every session alive at the moment this
 * ships predates the stamp, and treating "no start time" as "too old" would sign out every user of
 * every tenant on deploy. The idle rule is what catches the genuinely broken sessions; the cap only
 * has to start counting from here. It adopts the last known activity in preference to now, so a
 * session that has been open a while does not get its clock wound all the way back.
 */
export function sessionStartedAt() {
  const stored = readStamp(SESSION_STARTED_KEY)
  if (stored !== null) return stored
  const adopted = lastActivityAt() || Date.now()
  writeStamp(SESSION_STARTED_KEY, adopted)
  return adopted
}

/** Stamp a brand new session. Called at login, and only at login. */
export function startSession(at) {
  const now = at || Date.now()
  writeStamp(SESSION_STARTED_KEY, now)
  writeStamp(LAST_ACTIVITY_KEY, now)
}

/**
 * Where this session stands, as ONE decision both the timer and the refresh interceptor read.
 *
 * 'unknown' is reported separately from 'expired' so a caller can log the difference, but both must
 * end the session: not knowing when somebody was last here is a reason to ask them to sign in, not
 * a reason to say nothing.
 */
export function idleVerdict(now) {
  const at = now || Date.now()
  const last = lastActivityAt()
  if (last === null) return 'unknown'
  if (at - sessionStartedAt() >= absoluteMs()) return 'expired'
  const idle = at - last
  if (idle >= expiryMs()) return 'expired'
  if (idle >= idleMs()) return 'warning'
  // THE ABSOLUTE PATH GETS A WARNING TOO, and until this line it did not — it returned 'expired'
  // above and 'active' here, so a session that had simply been alive all day ended with no notice
  // at all. Somebody pricing a bid was dropped mid-keystroke and then told it was inactivity, which
  // is the original complaint wearing a different hat.
  //
  // Same two minutes as the idle path. `secondsUntilExpiry` already counts the absolute window down
  // correctly, so the modal needed no arithmetic it did not already have — only a verdict that asks
  // for it. Deliberately the LAST branch: an idle session that is also near its absolute cap should
  // still read as idle, because that is the clock the person can do something about.
  if (at - sessionStartedAt() >= absoluteMs() - WARNING_MS) return 'warning'
  return 'active'
}

/**
 * WHICH CLOCK is about to end this session. For WORDING ONLY — never for the decision, which stays
 * entirely inside `idleVerdict` above.
 *
 * It exists because "your session expired due to inactivity" is false on the absolute path and the
 * person it is shown to is, by definition, not inactive. Telling somebody who has been working all
 * day that they were idle is the kind of small untruth that teaches people the app is wrong about
 * them.
 *
 * Whichever window closes SOONER is the one doing the ending. An unknown activity stamp reports
 * 'idle', which keeps the existing message for the existing case.
 */
export function endReason(now) {
  const at = now || Date.now()
  const last = lastActivityAt()
  const untilAbsolute = sessionStartedAt() + absoluteMs() - at
  const untilIdle = last === null ? 0 : last + expiryMs() - at
  return untilAbsolute <= untilIdle ? 'absolute' : 'idle'
}

/** Seconds left on the countdown, for the warning modal. */
export function secondsUntilExpiry(now) {
  const at = now || Date.now()
  const last = lastActivityAt()
  const byIdle = last === null ? 0 : last + expiryMs() - at
  const byAbsolute = sessionStartedAt() + absoluteMs() - at
  return Math.max(0, Math.ceil(Math.min(byIdle, byAbsolute) / 1000))
}

/**
 * Count a request as activity — but only one a person actually asked for.
 *
 * MUTATIONS ONLY. This app runs three background pollers (the header's report poll every 60s, the
 * notification fallback every 30s, and an office-board socket), and if a GET counted, a tab left
 * open on a dashboard would hold a session open forever and the whole timeout would be theatre.
 * A POST/PUT/PATCH/DELETE is somebody pressing something.
 *
 * Auth traffic is excluded for the same reason at the other end: a token rotation is the app
 * talking to itself. Letting a refresh count as activity is exactly how two open tabs used to keep
 * each other signed in indefinitely with nobody at either keyboard.
 *
 * A caller with a background write can opt out with `config.backgroundRequest = true`.
 */
const MUTATING = new Set(['post', 'put', 'patch', 'delete'])

export function markApiActivity(config) {
  try {
    if (!config || config.backgroundRequest) return
    if (!MUTATING.has(String(config.method || '').toLowerCase())) return
    if (String(config.url || '').includes('/auth/')) return
    if (!window.localStorage.getItem(`${APP_STORAGE_PREFIX}_user`)) return
    markActivity()
  } catch { /* never let bookkeeping break a request */ }
}
